import Head from 'next/head';
import Link from 'next/link';
import {
  Anchor,
  AppShell,
  Box,
  Button,
  Center,
  Container,
  createStyles,
  Group,
  LoadingOverlay,
  Paper,
  Text,
  TextInput,
  Title,
  Transition,
} from '@mantine/core';
import { useSetState } from '@mantine/hooks';
import { useForm, zodResolver } from '@mantine/form';
import { IconArrowLeft, IconMail } from '@tabler/icons';
import { z } from 'zod';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/nextjs';
import { sendPasswordResetEmail } from '@/features/auth';

interface PasswordResetFormValues {
  email: string;
}

interface State {
  // 通信中かどうか
  loading: boolean;
  // メールが送信されたかどうか
  emailSent: boolean;
  // パスワードリセット処理に失敗したかどうか
  requestFailed: boolean;
  // API でエラーが発生した際のエラーコード
  errorCode: Maybe<number>;
}

const schema = z.object({
  email: z
    .string()
    .email('メールアドレスの形式が正しくありません')
    .min(1, 'メールアドレスを入力してください'),
});

const useStyles = createStyles((theme) => ({
  title: {
    fontSize: 26,
    fontWeight: 900,
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
  },

  controls: {
    [theme.fn.smallerThan('xs')]: {
      flexDirection: 'column-reverse',
    },
  },

  control: {
    [theme.fn.smallerThan('xs')]: {
      width: '100%',
      textAlign: 'center',
    },
  },
}));

/**
 * ページコンポーネント
 * @constructor
 */
const PasswordResetPage = (): JSX.Element => {
  const [state, setState] = useSetState<State>({
    loading: false,
    emailSent: false,
    requestFailed: false,
    errorCode: null,
  });
  const form = useForm<PasswordResetFormValues>({
    validate: zodResolver(schema),
    initialValues: { email: '' },
  });
  const { classes } = useStyles();

  const onSubmit = form.onSubmit(async ({ email }) => {
    try {
      setState({ loading: true });
      await sendPasswordResetEmail(email);
      setState({ emailSent: true, requestFailed: false });
    } catch (e: unknown) {
      setState({ emailSent: false, requestFailed: true });
      if (!(e instanceof AxiosError && e.response?.status === 400)) {
        // 想定外のエラーなので Sentry でキャプチャ
        Sentry.captureException(e);
      } else {
        setState({ errorCode: e?.response?.data?.error_code });
      }
    } finally {
      setState({ loading: false });
    }
  });

  return (
    <>
      <Head>
        <title>パスワードリセット | Ranking Master</title>
      </Head>

      <AppShell
        styles={(theme) => ({
          main: {
            backgroundColor:
              theme.colorScheme === 'dark'
                ? theme.colors.dark[8]
                : theme.colors.gray[0],
            height: '100vh',
          },
        })}
      >
        <Container size={460} my={30}>
          <Title className={classes.title} align="center">
            パスワードを忘れましたか？
          </Title>
          <Text color="dimmed" size="sm" align="center" mt="xs">
            メールアドレスを入力してください。
            <br />
            パスワードをリセットするためのメールを送信されます。
          </Text>

          <Paper shadow="md" p={30} radius="md" mt="xl">
            <LoadingOverlay visible={state.loading} />

            <form onSubmit={onSubmit}>
              <TextInput
                type="email"
                label="メールアドレス"
                icon={<IconMail />}
                required
                {...form.getInputProps('email')}
              />

              <Transition
                mounted={state.emailSent}
                transition="fade"
                duration={400}
                timingFunction="ease"
              >
                {(styles) => (
                  <Box style={styles} mt="lg">
                    <Text size="sm" color="gray">
                      パスワードリセットメールが送信されました。
                    </Text>
                    <Text size="sm" color="gray" mt="xs">
                      メールが届かない場合、メールアドレスに誤りが無いか確認し、
                      再度パスワードリセットをボタンをクリックしてください。
                    </Text>
                  </Box>
                )}
              </Transition>

              <Transition
                mounted={state.requestFailed}
                transition="fade"
                duration={400}
                timingFunction="ease"
              >
                {(styles) => (
                  <Box style={styles} mt="lg">
                    <Text size="sm" color="red">
                      {state.errorCode === 122
                        ? 'リンクの有効期限が切れています。もう一度はじめからやり直してください。'
                        : 'エラーが発生しました。再度お試しください。'}
                    </Text>
                  </Box>
                )}
              </Transition>

              <Group position="apart" mt="lg" className={classes.controls}>
                <Link href="/admin/login" passHref>
                  <Anchor color="dimmed" size="sm" className={classes.control}>
                    <Center inline>
                      <IconArrowLeft size={12} />
                      <Box ml={5}>サインインに戻る</Box>
                    </Center>
                  </Anchor>
                </Link>

                <Button type="submit" className={classes.control}>
                  パスワードをリセットする
                </Button>
              </Group>
            </form>
          </Paper>
        </Container>
      </AppShell>
    </>
  );
};

export default PasswordResetPage;
